import { AccountBoxRounded as AccountBoxRoundedIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { styled } from '@mui/material/styles';
import is from '@sindresorhus/is';
import { useContext } from 'react';

import { CardPlaceholder } from './common/CardPlaceholder';
import { Endorsements } from './Endorsements';
import { ApplicationContext } from '../../../../../../shared/source/context/context';
import {
    useUserHardSoftSkillsOptionsQuery,
    useUserHardSoftSkillsQuery,
} from '../../../../../../shared/source/query-hooks/hard-soft-skills';
import { HeadlineItem } from '../../../../design/components/collections/SkillsCollection';
import { ContentHeadline, ContentWrapper } from '../../../../design/components/containers/ContentWrapper';
import { SmallText } from '../../../../design/components/Typography';
import { useGroupSkillsByProficiency, useSkillProficiencyLevelName } from '../../../common/skills';
import { SkillChipWithDrawer } from '../../../common/skills/SkillChipWithDrawer';
import { useScrollToLocationHash } from '../../../use-scroll-to-location-hash';

import type { EndorsedHardSoftSkill } from '../../../../../../shared/source/data-contract/skill';
import type { FunctionComponent, ReactNode } from 'react';

const endorsementColumns = 2;

export const StyledListItem = styled(ListItem)(({ theme }) => ({
    '&:not(:last-child)': {
        borderBottom: '1px solid',
        borderBottomColor: theme.palette.cardborder.main,
    },
    alignItems: 'center',
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: `40% minmax(0px, 3fr) repeat(${endorsementColumns}, minmax(0, 2fr)) 1fr`,
    paddingBlock: theme.spacing(1),
    paddingInline: theme.spacing(3),
}));

export const StyledList = styled(List)(({ theme }) => ({
    '&:not(:last-child)': {
        borderBottom: '1px solid',
        borderBottomColor: theme.palette.cardborder.main,
    },
}));

type SkillListProps = {
    readonly skillLevel: EndorsedHardSoftSkill['level'];
    readonly skillList: readonly EndorsedHardSoftSkill[] | undefined;
};
const SkillList: FunctionComponent<SkillListProps> = (props: SkillListProps) => {
    const { getProficiencyLevelName } = useSkillProficiencyLevelName();

    if (is.undefined(props.skillList) || is.emptyArray(props.skillList)) {
        return null;
    }

    return (
        <StyledList
            disablePadding={true}
            subheader={
                <HeadlineItem
                    title={getProficiencyLevelName({
                        ...props.skillList[0]!,
                        level: props.skillLevel,
                    })}
                />
            }
        >
            {props.skillList.map((skill) => (
                <StyledListItem key={skill.id} disablePadding={true}>
                    <Box>
                        <SkillChipWithDrawer label={skill.title} skillId={skill.id} type={skill.skillType} />
                    </Box>
                    <SmallText>{getProficiencyLevelName(skill, true)}</SmallText>
                    <Endorsements maxColumns={endorsementColumns} skill={skill} />
                </StyledListItem>
            ))}
        </StyledList>
    );
};

type SkillsContentProps = {
    readonly actions?: ReactNode | undefined;
    readonly skills?: readonly EndorsedHardSoftSkill[] | undefined;
};

export const SkillsContent: FunctionComponent<SkillsContentProps> = ({ actions, skills }) => {
    const { translate } = useContext(ApplicationContext);
    const { groupedSkillsByProficiency } = useGroupSkillsByProficiency(skills);

    useScrollToLocationHash();

    if (is.undefined(groupedSkillsByProficiency)) return null;

    return (
        <ContentWrapper>
            <Box
                id="skills"
                sx={{
                    position: 'relative',
                    top: -75,
                }}
            />
            <ContentHeadline actions={actions} text={translate('pages.recruiter-profile.skills.title')} />
            {groupedSkillsByProficiency.length > 0 ? (
                groupedSkillsByProficiency.map(([level, skillList]) => (
                    <SkillList key={level} skillLevel={level} skillList={skillList} />
                ))
            ) : (
                <CardPlaceholder
                    placeholderIcon={<AccountBoxRoundedIcon />}
                    placeholderText={translate('pages.recruiter-profile.skills.placeholder')}
                />
            )}
        </ContentWrapper>
    );
};

type SkillsProps = {
    readonly actions?: ReactNode;
    readonly userId: string;
};

export const Skills: FunctionComponent<SkillsProps> = ({ actions, userId }) => {
    const { data } = useUserHardSoftSkillsOptionsQuery(userId);

    const { data: hardSoftSkillsData } = useUserHardSoftSkillsQuery({
        enabled: Boolean(data?.canReadSkills),
        userId,
    });

    return <SkillsContent actions={actions} skills={hardSoftSkillsData} />;
};
